import { useContext } from 'react'

import type { UseFormOptions } from '@domain-group/react-hooks'
import { useForm, useLatestEntity } from '@domain-group/react-hooks'

import { launchModal } from '@domain-group/javascript-utils'

import useDispatch from '@hooks/useDispatch'
import useSelector from '@hooks/useSelector'

import * as invoiceItemActions from '@redux/modules/invoiceItem'

import defaultFormState, { requiredFields } from '@models/invoiceItem'

import PageContext from '@contexts/pageContext'

import type { InvoiceItemModel, InvoiceItemRequestOptions, InvoiceModel } from '@models/types'
import type { DeleteParams } from '@redux/modules/types'
import type { AppDispatch } from '@redux/store'

type CreateInvoiceItemParams = {
  dispatch: AppDispatch,
  invoiceItemParams: Partial<InvoiceItemModel>,
  requestOptions?: InvoiceItemRequestOptions,
}

const createInvoiceItem = (params: CreateInvoiceItemParams) => {
  const { dispatch, invoiceItemParams, requestOptions } = params
  const { createInvoiceItem: createFn } = invoiceItemActions

  return dispatch(createFn(invoiceItemParams, requestOptions))
}

type UpdateInvoiceItemParams = {
  dispatch: AppDispatch,
  invoiceItem: InvoiceItemModel,
  invoiceItemParams: Partial<InvoiceItemModel>,
  requestOptions?: InvoiceItemRequestOptions,
}

const updateInvoiceItem = (params: UpdateInvoiceItemParams) => {
  const {
    dispatch, invoiceItem, invoiceItemParams, requestOptions,
  } = params
  const { updateInvoiceItem: updateFn } = invoiceItemActions

  const updatedParams = {
    id: invoiceItem.id,
    ...invoiceItemParams,
  }

  return dispatch(updateFn(updatedParams, requestOptions))
}

type DeleteInvoiceItemParams = {
  dispatch: AppDispatch,
  invoiceItem: DeleteParams<InvoiceItemModel>,
}

const deleteInvoiceItem = (params: DeleteInvoiceItemParams) => {
  const { dispatch, invoiceItem } = params
  const { deleteInvoiceItem: deleteFn } = invoiceItemActions

  return dispatch(deleteFn(invoiceItem))
}

type ReprocessInvoiceItemToZuoraParams = {
  dispatch: AppDispatch,
  invoiceItem: Partial<InvoiceItemModel>,
}

const reprocessInvoiceItemToZuora = (params: ReprocessInvoiceItemToZuoraParams) => {
  const { dispatch, invoiceItem } = params
  const { reprocessInvoiceItemToZuora: reprocessFn } = invoiceItemActions

  return dispatch(reprocessFn(invoiceItem))
}

type CustomFormOptions = {
  customRequiredFields?: UseFormOptions['requiredFields'],
}

export function useInvoiceItemForm(
  invoiceItem: Partial<InvoiceItemModel>,
  options: UseFormOptions & CustomFormOptions = {},
) {
  const { customRequiredFields = [], validateOn } = options || {}

  const invoiceItemForm = useForm(
    defaultFormState,
    { entity: invoiceItem, requiredFields: [...requiredFields, ...customRequiredFields], validateOn },
    [invoiceItem.id, invoiceItem.cache_key],
  )

  return {
    ...invoiceItemForm,
  }
}

export function useRelations(invoiceItem: Partial<InvoiceItemModel> = {}) {
  const { invoice_id } = invoiceItem

  const invoices = useSelector(reduxState => reduxState.entities.invoices)

  const invoice: Partial<InvoiceModel> = invoice_id && invoices[invoice_id] ? invoices[invoice_id] : {}

  return {
    invoice,
  }
}

function useInvoiceItem(initEntity: Partial<InvoiceItemModel> = {}) {
  // @ts-expect-error TS2322
  const { entity: invoiceItem }: { entity: InvoiceItemModel} = useLatestEntity(initEntity, 'invoiceItems')

  const dispatch = useDispatch()

  const { callbacks } = useContext(PageContext)

  const {
    creating, deleting, loadedIds, loading, updating,
  } = useSelector(reduxState => reduxState.invoiceItems)

  const { invoice } = useRelations(invoiceItem)

  return {
    callbacks: {
      createInvoiceItem: (invoiceItemParams: Partial<InvoiceItemModel>, entityOptions?: InvoiceItemRequestOptions) => (
        createInvoiceItem({ invoiceItemParams, dispatch, requestOptions: entityOptions })
      ),
      createOrEditInvoiceItem: () => launchModal({
        callbacks,
        modalKey: 'CreateOrEditInvoiceItemModal',
        payload: { invoiceItem },
      }),
      deleteInvoiceItem: () => deleteInvoiceItem({ dispatch, invoiceItem }),
      reprocessInvoiceItemToZuora: () => reprocessInvoiceItemToZuora({ dispatch, invoiceItem }),
      updateInvoiceItem: (invoiceItemParams: Partial<InvoiceItemModel>, entityOptions?: InvoiceItemRequestOptions) => (
        updateInvoiceItem({
          invoiceItem, invoiceItemParams, dispatch, requestOptions: entityOptions,
        })
      ),
    },
    creating,
    deleting,
    invoice,
    invoiceItem,
    loading: loading && !loadedIds.includes(invoiceItem.id),
    updating,
  }
}

export type UseInvoiceItemPayload = ReturnType<typeof useInvoiceItem>

export default useInvoiceItem
